import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import { ExtendedLink as Link } from 'components/common/LocaleLink';
import { getDynamicContentUrl } from 'helpers/link';

type ResourceType = 'blogPost' | 'integration' | 'customerStory' | 'webinar' | 'resource';

type LocalizedItem = {
    en: string;
};

type BlogPostResource = {
    _type: ResourceType;
    identifier: string;
    localizedTitle: LocalizedItem;
    description: LocalizedItem;
    image: any;
    _rawSlug: any;
};

type IntegrationResource = {
    _type: ResourceType;
    title: string;
    description: LocalizedItem;
    logo: any;
};

type ContentDownloadResource = {
    _type: ResourceType;
    localizedTitle: LocalizedItem;
    identifier: string;
    description: LocalizedItem;
    contentCoverImage: any;
};

type WebinarResource = {
    _type: ResourceType;
    title: string;
    subtitle: string;
    image: any;
};

type CustomerStoryResource = {
    _type: ResourceType;
    companyName: string;
    customerAndUs: LocalizedItem;
    image: any;
};

type RelatedResourcesProps = {
    title?: string;
    showElementDescription?: boolean;
    relatedResources:
        | BlogPostResource[]
        | IntegrationResource[]
        | ContentDownloadResource[]
        | WebinarResource[]
        | CustomerStoryResource[];
};

const getHorizontalContentElement = (
    title: string,
    description: string,
    image: any,
    type: ResourceType,
    identifier?: string,
    _rawSlug?: any,
    showElementDescription: boolean = true
) => {
    return (
        <Link
            key={identifier}
            to={
                _rawSlug
                    ? `/${getDynamicLinkFromType(type)}/${_rawSlug.current}`
                    : getDynamicContentUrl(identifier ?? title, getDynamicLinkFromType(type))
            }
            className="tw-text tw-no-underline tw-group"
        >
            <div className="tw-relative tw-grid md:tw-grid-cols-[200px_1fr] md:tw-gap-0 tw-gap-24">
                <div>
                    <GatsbyImage
                        image={image.asset.gatsbyImageData}
                        alt={title}
                        className="tw-rounded tw-w-[180px] tw-h-auto"
                    />
                </div>
                <div>
                    <h3 className="tw-font-bold group-hover:tw-text-primary tw-transition-all">
                        {title}
                    </h3>
                    {showElementDescription && <p className="tw-mt-4 tw-text-sm">{description}</p>}
                    <p className="tw-mt-12 tw-underline">View content</p>
                </div>
            </div>
        </Link>
    );
};

const getIntegrationContentElement = (
    title: string,
    description: string,
    image: any,
    type: ResourceType,
) => {
    return (
        <Link
            key={title}
            to={getDynamicContentUrl(title, getDynamicLinkFromType(type))}
            className="tw-text tw-no-underline group"
        >
            <div className="tw-relative">
                <GatsbyImage
                    image={image.asset.gatsbyImageData}
                    alt={title}
                    className="tw-rounded tw-w-[180px] tw-mb-12"
                />
                <p className="tw-text-sm">{description}</p>
                <p className="tw-mt-12 tw-underline">View content</p>
            </div>
        </Link>
    );
};

const getDynamicLinkFromType = (type: ResourceType) => {
    switch (type) {
        case 'blogPost':
            return 'blog';
        case 'integration':
            return 'integrations';
        case 'customerStory':
            return 'customers';
        case 'webinar':
            return 'webinars';
        case 'resource':
            return 'resources';
    }
};

export const RelatedResources = ({
    relatedResources,
    title = 'Learn more',
    showElementDescription = true
}: RelatedResourcesProps) => {
    const getBlogPostResourceElement = (blogPost: BlogPostResource) => {
        return getHorizontalContentElement(
            blogPost.localizedTitle.en,
            blogPost.description.en,
            blogPost.image,
            'blogPost',
            blogPost.identifier,
            blogPost._rawSlug,
            showElementDescription
        );
    };
    const getWebinarResourceItem = (webinar: WebinarResource) => {
        return getHorizontalContentElement(
            webinar.title,
            webinar.subtitle,
            webinar.image,
            'webinar',
            null,
            null,
            showElementDescription

        );
    };

    const getContentDownloadResourceItems = (contentDownload: ContentDownloadResource) => {
        return getHorizontalContentElement(
            contentDownload.localizedTitle.en,
            contentDownload.description.en,
            contentDownload.contentCoverImage,
            'resource',
            contentDownload.identifier,
            showElementDescription
        );
    };

    const getIntegrationResourceItems = (integration: IntegrationResource) => {
        return getIntegrationContentElement(
            integration.title,
            integration.description.en,
            integration.logo,
            'integration'
        );
    };

    const getCustomerStoryResourceItem = (customerStory: CustomerStoryResource) => {
        return getHorizontalContentElement(
            customerStory.companyName,
            customerStory.customerAndUs.en,
            customerStory.image,
            'customerStory',
            null,
            null,
            showElementDescription
        );
    };

    const getRelatedResourceElement = (relatedResource: any) => {
        switch (relatedResource._type) {
            case 'blogPost':
                return getBlogPostResourceElement(relatedResource);
            case 'webinar':
                return getWebinarResourceItem(relatedResource);
            case 'integration':
                return getIntegrationResourceItems(relatedResource);
            case 'resource':
                return getContentDownloadResourceItems(relatedResource);
            case 'customerStory':
                return getCustomerStoryResourceItem(relatedResource);
            default:
                break;
        }
    };

    return (
        <div className="tw-w-full tw-py-[100px]">
            <h2 className="tw-mb-12 tw-pb-12 tw-border-b tw-text-xl">{title}</h2>
            <div className="tw-grid xl:tw-grid-cols-3 lg:tw-grid-cols-2 md:tw-grid-cols-1 tw-gap-x-48 tw-gap-y-64 tw-mt-24">
                {relatedResources.map((relatedResource, index) => {
                    return <span key={index}>{getRelatedResourceElement(relatedResource)}</span>;
                })}
            </div>
        </div>
    );
};
